import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, Loader2, History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "What's Driving This Trend" AI narrator + Previous-reads history.
 * The parent board supplies the already-computed composite + component figures
 * (0–100 with signed deltas); this card handles generate/refresh/caching/history.
 */
export default function TrendInsightNarrator({
  engine, engineLabel, compositeLabel, organizationId, cohortId = 'all',
  composite, components, testId = 'trend-insight',
}) {
  const [insight, setInsight] = useState(null);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [compareSel, setCompareSel] = useState([]);

  const toggleCompare = useCallback((i) => {
    setCompareSel((prev) => {
      if (prev.includes(i)) return prev.filter((x) => x !== i);
      return [...prev, i].slice(-2);
    });
  }, []);

  const headers = useCallback(() => {
    const token = secureStorage.getItem('session_token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const ready = composite && composite.now != null && (components || []).length >= 2;
  const payload = useCallback((extra) => ({
    engine, engine_label: engineLabel, composite_label: compositeLabel,
    organization_id: organizationId, cohort_id: cohortId, composite, components, ...extra,
  }), [engine, engineLabel, compositeLabel, organizationId, cohortId, composite, components]);

  // Auto-load a cached read on cohort/data change (never spends).
  useEffect(() => {
    if (!ready) { setInsight(null); setCached(false); return; }
    authedFetch(`${API_URL}/api/trend-insight`, { method: 'POST', headers: headers(), body: JSON.stringify(payload({ cached_only: true })) })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { setInsight(d?.available ? d.insight : null); setCached(!!d?.available); })
      .catch(() => { setInsight(null); setCached(false); });
  }, [ready, payload, headers]);

  const loadHistory = useCallback(() => {
    const params = new URLSearchParams({ engine, cohort_id: cohortId });
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    authedFetch(`${API_URL}/api/trend-insight/history?${params}`, { headers: { Authorization: headers().Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setHistory(d?.history || [])).catch(() => setHistory([]));
  }, [engine, cohortId, organizationId, headers]);

  const generate = useCallback(async (force) => {
    if (!ready) { toast.error('Not enough trend history to summarise yet.'); return; }
    setLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/trend-insight`, { method: 'POST', headers: headers(), body: JSON.stringify(payload({ force: !!force })) });
      if (res.ok) {
        const d = await res.json();
        if (d?.available) { setInsight(d.insight); setCached(!!d.cached); if (showHistory) loadHistory(); }
        else toast.error('Not enough trend history to summarise yet.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not generate the summary.');
      }
    } catch { toast.error('Could not generate the summary.'); }
    finally { setLoading(false); }
  }, [ready, payload, headers, showHistory, loadHistory]);

  const toggleHistory = useCallback(() => {
    setShowHistory((prev) => { const next = !prev; if (next) loadHistory(); return next; });
  }, [loadHistory]);

  return (
    <Card data-testid={`${testId}-card`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-5 w-5 text-fuchsia-600" /> What's Driving This Trend
            </CardTitle>
            <p className="text-xs text-slate-500">A plain-English read on which {engineLabel} component is moving the score and the one move to lift the weakest.</p>
          </div>
          <button onClick={() => generate(!!insight)} disabled={loading || !ready} data-testid={`${testId}-generate`}
            className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors shrink-0',
              (loading || !ready) ? 'opacity-60 border-slate-200 text-slate-400' : 'border-fuchsia-300 text-fuchsia-700 hover:border-fuchsia-500 hover:bg-fuchsia-50')}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (insight ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
            {loading ? 'Generating…' : (insight ? 'Refresh' : 'Generate summary')}
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {insight ? (
          <div data-testid={`${testId}-text`}>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{insight}</p>
            <div className="flex items-center gap-3 mt-2">
              {cached && <p className="text-[10px] text-slate-400">Cached — click Refresh to regenerate from the latest numbers.</p>}
              <button onClick={toggleHistory} data-testid={`${testId}-history-toggle`}
                className="text-[11px] font-semibold text-fuchsia-600 hover:text-fuchsia-700 inline-flex items-center gap-1 transition-colors">
                <History className="h-3.5 w-3.5" /> {showHistory ? 'Hide previous reads' : 'Previous reads'}
              </button>
            </div>
            {showHistory && (
              <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`${testId}-history`}>
                {history.length ? (
                  <>
                    <p className="text-[10px] text-slate-400 mb-2">Tick two reads to compare them side by side.</p>
                    {compareSel.length === 2 && (
                      <div className="grid grid-cols-2 gap-3 mb-3" data-testid={`${testId}-compare`}>
                        {compareSel.map((idx) => {
                          const hh = history[idx];
                          return (
                            <div key={idx} className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/10 p-2.5">
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-500 mb-1">
                                {hh?.generated_at ? new Date(hh.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Earlier'}
                              </p>
                              <p className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-line">{hh?.insight}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="space-y-3">
                      {history.map((hh, i) => (
                        <div key={i} className="text-xs flex gap-2" data-testid={`${testId}-history-item-${i}`}>
                          <input type="checkbox" checked={compareSel.includes(i)} onChange={() => toggleCompare(i)}
                            className="mt-0.5 accent-fuchsia-600" data-testid={`${testId}-history-check-${i}`} />
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">
                              {hh.generated_at ? new Date(hh.generated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Earlier'}
                            </p>
                            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{hh.insight}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <p className="text-xs text-slate-400">No earlier summaries yet — each time you Refresh, the previous read is kept here.</p>}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid={`${testId}-empty`}>
            {ready ? 'No summary yet. Click "Generate summary" for an AI read of the trend.' : 'Not enough trend history to summarise yet.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
